/**
 * TargetedImagePicker — platform-aware image selector for ad creation.
 *
 * Wraps `ImagePicker` + `CropAdjustModal` to produce per-(platform, placement)
 * pending crops, sourced from either an Approved image (cropper runs) or an
 * existing Targeted crop (skipped — it's already a placement-fit derivative).
 *
 * Two paths via the inner picker's tab strip:
 *   - **Approved tab** (default): user picks a source, the CropAdjustModal
 *     runs in sequence over the selection. Each crop save produces a
 *     `PendingTargeted` (source + placementKey + cropBox).
 *   - **Targeted tab**: filtered server-side via
 *     `compatible_with_placement_key` (`MediaCRUD.get_images`) — surfaces
 *     any prior crop whose origin spec shares the active aspect ratio,
 *     whose parent crop_box pixels are large enough under the backend's
 *     upscale cap, and whose filesize fits the active placement's max,
 *     including cross-placement and cross-platform reuse. The client-side
 *     `./targetedCompatibility.ts` helper runs on the fetched rows as a
 *     defensive backstop for spec-registry drift, but only re-checks
 *     aspect ratio + filesize (pixel adequacy is exclusively server-side
 *     because it depends on the env-configurable upscale cap).
 *
 *     Picking from this tab synthesizes a `PendingTargeted` directly from
 *     the row's persisted `source_image_id` + `crop_box`.
 *
 *     - Same placement_key → backend dedup in `_find_existing_targeted`
 *       returns the existing row (no new GCS blob, no new DB row).
 *     - Cross-placement → backend creates a new Targeted row for the
 *       active placement, re-encoded for that placement's recommended
 *       dims. (Storage cost is bounded by the lifecycle TTL in
 *       `docs/TARGETED_IMAGE_LIFECYCLE.md` §B.)
 *
 * **No GCS write happens here in either path.** Once `onConfirm` fires with
 * the `PendingTargeted[]`, the caller decides when to materialize — typically
 * at "Upload to Google Ads" click time. This keeps GCS clean of abandoned
 * crop iterations.
 *
 * Previews of pending entries are rendered via `CroppedThumbnail`. For
 * Approved-source pending entries it does its CSS-percent crop math; for
 * Targeted-reuse pending entries (where `source.category === 'targeted'`)
 * it short-circuits and renders the already-cropped bytes full-bleed.
 *
 * For unchanged legacy flows that still want raw image IDs (no platform scope),
 * keep using `ImagePicker` directly.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ImagePicker } from './ImagePicker';
import { CropAdjustModal, type CropAdjustModalConfirmPayload } from './CropAdjustModal';
import { criteriaFromSpec } from './targetedCompatibility';
import {
  findSpecByKey,
  usePlatformSpecs,
} from '@/hooks/queries/usePlatformSpecs';
import type { MediaImage, PendingTargeted } from '@/types/media';
import type { Platform } from '@/types/platformSpecs';
import { toast } from '@/utils';

export interface UsageContext {
  platform: Platform;
  /** e.g. 'google_ads.square_marketing_image' */
  placementKey: string;
}

export interface TargetedImagePickerProps {
  isOpen: boolean;
  onClose: () => void;
  usageContext: UsageContext;
  /** Called once per crop confirmation cycle with the pending crops.
   *  Caller is responsible for materializing them when ready (typically at
   *  upload-to-Google-Ads time). May return a promise — the picker awaits
   *  it before closing, showing a "finalizing" overlay so the
   *  multi-second materialization round-trip isn't invisible. */
  onConfirm: (pending: PendingTargeted[]) => void | Promise<void>;
  maxSelection?: number;
  initialFolderId?: string;
  initialTagIds?: string[];
  // NOTE: no `selectedTargetedIds` / preselect prop. The picker's
  // cross-tab selection cache resets on open, and cross-ad reuse is one
  // pick at a time — preselect would only matter for a "resume in-progress
  // selection" flow that doesn't exist yet.
}

type CropQueueState =
  | { status: 'idle' }
  | { status: 'cropping'; queue: MediaImage[]; index: number; resolved: PendingTargeted[] }
  | { status: 'finalizing'; resolved: PendingTargeted[] }
  | { status: 'done' };

export function TargetedImagePicker({
  isOpen,
  onClose,
  usageContext,
  onConfirm,
  maxSelection,
  initialFolderId,
  initialTagIds,
}: TargetedImagePickerProps) {
  const { data: specs } = usePlatformSpecs({ platform: usageContext.platform });
  const spec = useMemo(
    () => findSpecByKey(specs, usageContext.placementKey),
    [specs, usageContext.placementKey]
  );
  // Memoize the compatibility criteria so the inner ImagePicker's
  // `useMemo`-backed filter (deps include this object) doesn't re-run on
  // every parent render. `criteriaFromSpec` returns a fresh object each
  // call; without this memo, a new reference flows through to ImagePicker
  // every render and the .filter() over up to N Targeteds re-executes.
  const compatCriteria = useMemo(
    () => (spec ? criteriaFromSpec(spec) : undefined),
    [spec],
  );

  // The Approved/Targeted tab strip on the inner `ImagePicker` is what makes
  // Targeted-reuse possible — picking from the Targeted tab returns rows whose
  // `crop_box` + `source_image` are already set, so we can synthesize a
  // `PendingTargeted` directly without running the cropper. See
  // `handlePickerConfirm` below for the branched flow.

  const [cropQueue, setCropQueue] = useState<CropQueueState>({ status: 'idle' });

  // When the picker closes (parent flips `isOpen` to false), reset the
  // internal queue back to `idle`. Without this, after a successful crop
  // session the queue ends at `done` and stays there — re-opening the
  // picker keeps the inner ImagePicker hidden (its `isOpen` is gated on
  // `isOpen && cropQueue.status === 'idle'`) and the user sees a "dead"
  // Select-from-Library button.
  useEffect(() => {
    if (!isOpen && cropQueue.status !== 'idle') {
      setCropQueue({ status: 'idle' });
    }
  }, [isOpen, cropQueue.status]);

  // ImagePicker passes the resolved `MediaImage[]` as a third arg, drawn from
  // its own list which already reflects the picker's current filters
  // (folder, tags, search). Using that directly is correct regardless of
  // which filters the user applied — earlier attempts to re-resolve IDs from
  // a separately-fetched list silently dropped selections when the local
  // query's filters didn't match the picker's.
  const handlePickerConfirm = useCallback(
    (imageIds: string[], _imageType?: string, images?: MediaImage[]) => {
      // Empty selection → close without an error.
      if (imageIds.length === 0) {
        onConfirm([]);
        onClose();
        return;
      }

      const queue = images ?? [];
      if (queue.length !== imageIds.length) {
        // ImagePicker's resolution should match its own list 1:1. If we landed
        // here, something is genuinely wrong (stale cache, race) — surface it
        // rather than silently closing with a partial result.
        toast.error(
          `Could not load ${imageIds.length - queue.length} of ${imageIds.length} ` +
          'selected image(s). Try refreshing the picker.'
        );
        return;
      }

      // Branch on category. ImagePicker preserves selections across tab
      // switches (the cross-tab `selectedImageCache` survives filter
      // changes by design), so a confirmed queue can be a mix of Approved
      // and Targeted rows. Split rather than assuming uniformity, then
      // route each subset through its appropriate path: Approved → cropper
      // queue, Targeted → reuse-synthesis path. The two `resolved` lists
      // converge in the finalize step.
      const approvedQueue = queue.filter((img) => img.category !== 'targeted');
      const targetedQueue = queue.filter((img) => img.category === 'targeted');

      // Spec is only required when we're about to enter the cropper queue
      // (Approved selections). The Targeted-reuse path reads only
      // `usageContext.placementKey` + the row's own `crop_box` and
      // `source_image_id`, so a Targeted-only confirm can proceed even if
      // `usePlatformSpecs` is still resolving. Without this narrowing, a
      // brief load window (or an unknown placement key) would block the
      // entire confirm — including the path that doesn't need the spec.
      if (approvedQueue.length > 0 && !spec) {
        toast.error('Placement spec not available yet — please retry in a moment.');
        return;
      }

      // Targeted-reuse path: synthesize a PendingTargeted from each Targeted
      // row directly. No cropper, no GCS write — materialization will hit
      // `_find_existing_targeted` (keyed on member+source+placement+box) and
      // return the existing row.
      //
      // Stand-in shape: spread the Targeted's own fields and override `id`
      // to the parent Approved's id (`source_image_id`). Two consumers downstream:
      //   - `useMaterializePendingTargeted` reads `source.id` as the source
      //     image id when calling `createTargetedImage` → ✓ (it's the parent
      //     Approved's id, which is what the backend expects).
      //   - `CroppedThumbnail` reads `source.category === 'targeted'` to
      //     short-circuit its CSS-crop math and render the (already-cropped)
      //     bytes at `signed_url` directly → ✓ (the spread preserves
      //     `category: 'targeted'` and the Targeted's own `signed_url`).
      // The cropBox is the Targeted's persisted crop in parent-Approved
      // coordinates, which is exactly what the dedup hash compares against.
      const reuseResolved: PendingTargeted[] = [];
      const skipped: string[] = [];
      for (const t of targetedQueue) {
        // Defensive guard for legacy rows: an orphaned Targeted (cascaded
        // FK should prevent this, but we don't trust it) or a row written
        // before crop_box was a column would fail materialization. Skip
        // those rather than ship a broken PendingTargeted upstream.
        if (!t.source_image_id || !t.crop_box) {
          skipped.push(t.original_filename);
          continue;
        }
        reuseResolved.push({
          source: { ...t, id: t.source_image_id } as MediaImage,
          placementKey: usageContext.placementKey,
          cropBox: t.crop_box,
          // Carry the originating Targeted's id so consumers' `pendingKey`
          // can distinguish two existing Targeteds that happen to share
          // the same (source.id, cropBox) but were originally cropped for
          // different placements. Without this, both reuses would render
          // under the same React key in the same slot.
          existingTargetedId: t.id,
        });
      }
      if (skipped.length > 0) {
        toast.error(
          `Skipped ${skipped.length} reusable crop${skipped.length === 1 ? '' : 's'} ` +
          `with missing source data: ${skipped.join(', ')}`
        );
      }

      // Approved path: enqueue into the cropper. If both lists are non-empty
      // (theoretically impossible from the single-tab UI, but defended-for),
      // fire the reuse path immediately and run the cropper for the rest;
      // both batches feed the same finalize step via `resolved` accumulation.
      if (approvedQueue.length > 0) {
        setCropQueue({
          status: 'cropping',
          queue: approvedQueue,
          index: 0,
          resolved: reuseResolved,
        });
        return;
      }

      // Pure-reuse path (no Approved selections): skip straight to finalize.
      if (reuseResolved.length === 0) {
        // Every Targeted row was skipped (legacy rows missing crop_box or
        // source_image_id). Hand the caller an empty selection — same shape
        // as the empty-selection short-circuit at the top — so its
        // no-selection code path runs, then close.
        onConfirm([]);
        onClose();
        return;
      }
      setCropQueue({ status: 'finalizing', resolved: reuseResolved });
    },
    [spec, usageContext.placementKey, onConfirm, onClose]
  );

  const currentSource =
    cropQueue.status === 'cropping' ? cropQueue.queue[cropQueue.index] ?? null : null;

  const handleCropConfirm = useCallback(
    (payload: CropAdjustModalConfirmPayload) => {
      // Pure state update — advance the queue. The "we're done, fire onConfirm"
      // side effect runs in the useEffect below so we don't call into the
      // parent (which calls setState) during this updater. React's strict mode
      // surfaces in-render setState as a console warning otherwise.
      setCropQueue((prev) => {
        if (prev.status !== 'cropping') return prev;
        const pending: PendingTargeted = {
          source: payload.source,
          placementKey: usageContext.placementKey,
          cropBox: payload.cropBox,
        };
        return {
          ...prev,
          index: prev.index + 1,
          resolved: [...prev.resolved, pending],
        };
      });
    },
    [usageContext.placementKey]
  );

  // Two effects, separated so the "kick off finalize" run isn't
  // accidentally cancelled by the very next render's cleanup.
  //
  // 1) Crop queue exhausted → flip to `finalizing` (renders the overlay).
  useEffect(() => {
    if (
      cropQueue.status === 'cropping' &&
      cropQueue.index >= cropQueue.queue.length
    ) {
      setCropQueue({ status: 'finalizing', resolved: cropQueue.resolved });
    }
  }, [cropQueue]);

  // 2) On entering `finalizing`, fire onConfirm and await it. We track
  //    whether we've already kicked it off via a ref so re-renders during
  //    the await don't double-call. After the promise resolves, transition
  //    to `done` and close. On rejection, surface a toast and reopen the
  //    picker (revert to `idle`) so the user can retry — without an explicit
  //    catch the rejection would surface as an unhandled-promise warning AND
  //    `.finally` would close the modal silently as if the upload succeeded.
  const finalizeKickedRef = useRef(false);
  useEffect(() => {
    if (cropQueue.status !== 'finalizing') {
      finalizeKickedRef.current = false;
      return;
    }
    if (finalizeKickedRef.current) return;
    finalizeKickedRef.current = true;
    Promise.resolve(onConfirm(cropQueue.resolved)).then(
      () => {
        setCropQueue({ status: 'done' });
        onClose();
      },
      (err) => {
        const msg = err instanceof Error ? err.message : 'Failed to finalize crops';
        toast.error(msg);
        // Revert to `idle` so the inner ImagePicker reopens and the user can
        // re-pick + re-crop. We deliberately don't `onClose()` here — keeping
        // the modal open signals that retry is available without a re-launch.
        setCropQueue({ status: 'idle' });
      },
    );
  }, [cropQueue, onConfirm, onClose]);

  const handleCropCancel = useCallback(() => {
    // Bail out of the entire sequence — partial crops persisted in earlier
    // iterations stay in the library (they're keyed by source+placement+box,
    // so re-entering the flow will surface them under "previously cropped").
    setCropQueue({ status: 'idle' });
    onClose();
  }, [onClose]);

  const isCropOpen = cropQueue.status === 'cropping' && !!currentSource && !!spec;
  const isFinalizing = cropQueue.status === 'finalizing';

  return (
    <>
      <ImagePicker
        isOpen={isOpen && cropQueue.status === 'idle'}
        onClose={onClose}
        // Don't seed Targeted ids — the picker uses a tab-strip surface and
        // the cross-tab selection cache resets on open anyway.
        selectedImageIds={[]}
        onConfirm={handlePickerConfirm}
        maxSelection={maxSelection}
        targetAspectRatio={undefined}
        // Two-tab surface: Approved is the default (matches user-confirmed
        // preference for showing the smaller / more-frequently-used set
        // first). Targeted tab surfaces previously-cropped, ready-to-reuse
        // crops filtered to this placement.
        categoryTabs={[
          {
            value: 'approved',
            label: 'Approved',
            subtitle: 'Pick a source — the cropper will run next.',
          },
          {
            value: 'targeted',
            label: 'Targeted',
            subtitle:
              'Any pre-cropped crop with a matching aspect ratio and ' +
              'sufficient resolution — ready to reuse.',
          },
        ]}
        // Server-side compatibility filter — narrows the Targeted tab to
        // rows reusable for the active placement (same aspect ratio,
        // sufficient parent crop_box pixels under the upscale cap, filesize
        // under the placement's max). Cross-placement and cross-platform
        // reuse fall out for free.
        compatibleWithPlacementKey={usageContext.placementKey}
        // Defensive client-side backstop. Normally a no-op because the
        // server already narrows; runs after the fetch in case the server's
        // registry drifts from the frontend's spec hook. Memoized in
        // `compatCriteria` so the picker's filter dep stays stable.
        targetedCompatibility={compatCriteria}
        initialFolderId={initialFolderId}
        initialTagIds={initialTagIds}
        // Don't auto-close after the user confirms a selection — we still need
        // to run the crop queue (or, for Targeted reuse, the finalize step).
        // Closing happens after all crops resolve (or the user cancels).
        closeOnConfirm={false}
      />
      <CropAdjustModal
        isOpen={isCropOpen}
        source={currentSource}
        spec={spec ?? null}
        onClose={handleCropCancel}
        onConfirm={handleCropConfirm}
      />
      {/* Finalizing overlay — shown while the caller is materializing the
          pending crops (a multi-second GCS round-trip). Without this the user
          sees the modal close and nothing else for 2–5 seconds. */}
      {isFinalizing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          role="status"
          aria-live="polite"
          aria-label="Finalizing crops"
        >
          <div className="bg-white rounded-lg shadow-xl px-6 py-5 flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-gray-700">Finalizing crops…</span>
          </div>
        </div>
      )}
    </>
  );
}
